<template>
  <div class="polls-tab">
    <div class="polls-tab__scroll">
      <!-- MODERATOR VIEW -->
      <template v-if="isModerator">
        <button
          v-if="!showCreateForm"
          class="polls-tab__create-btn"
          @click="showCreateForm = true"
        >
          <v-icon
            x-small
            class="mr-1"
          >
            mdi-plus-circle
          </v-icon>
          New Poll
        </button>

        <CreatePollForm
          v-if="showCreateForm"
          @created="showCreateForm = false"
          @cancel="showCreateForm = false"
        />

        <div
          v-for="poll in sortedPolls"
          :key="poll.id"
          class="polls-tab__poll-item"
        >
          <PollResultsCard
            :poll="poll"
            :results="poll.results || []"
            :is-active="poll.is_active"
            :show-close="isModerator && poll.is_active"
            :closing="closingPollId === poll.id"
            @close="handleClose(poll.id)"
          />
        </div>

        <div
          v-if="polls.length === 0 && !showCreateForm"
          class="polls-tab__empty"
        >
          <v-icon
            color="grey lighten-1"
            size="32"
          >
            mdi-poll
          </v-icon>
          <span>No polls yet. Create one above.</span>
        </div>
      </template>

      <!-- USER VIEW -->
      <template v-else>
        <div v-if="activePolls.length > 0">
          <template v-for="poll in activePolls">
            <!-- Show results if user already voted (poll has results) -->
            <PollResultsCard
              v-if="hasVoted(poll)"
              :key="`result-${poll.id}`"
              :poll="poll"
              :results="poll.results || []"
              :is-active="true"
            />
            <!-- Otherwise show the vote card -->
            <PollVoteCard
              v-else
              :key="`vote-${poll.id}`"
              :poll="poll"
            />
          </template>
        </div>

        <div v-if="closedPolls.length > 0">
          <div class="polls-tab__section-title">
            <v-icon
              x-small
              class="mr-1"
            >
              mdi-chart-bar
            </v-icon>
            Results
          </div>
          <PollResultsCard
            v-for="poll in closedPolls"
            :key="poll.id"
            :poll="poll"
            :results="poll.results || []"
            :is-active="false"
          />
        </div>

        <div
          v-if="polls.length === 0"
          class="polls-tab__empty"
        >
          <v-icon
            color="grey lighten-1"
            size="32"
          >
            mdi-poll
          </v-icon>
          <span>No polls yet.</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import PollVoteCard from './PollVoteCard.vue';
import PollResultsCard from './PollResultsCard.vue';
import CreatePollForm from './CreatePollForm.vue';

export default {
  name: 'PollsTab',

  components: { PollVoteCard, PollResultsCard, CreatePollForm },

  props: {
    isModerator: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      showCreateForm: false,
      closingPollId: null,
    };
  },

  computed: {
    ...mapGetters('webinarChat', ['polls', 'activePolls', 'closedPolls']),

    sortedPolls() {
      return [...this.polls].sort((a, b) => b.is_active - a.is_active);
    },
  },

  methods: {
    ...mapActions('webinarChat', ['closePoll']),

    hasVoted(poll) {
      return poll.results && poll.results.length > 0;
    },

    async handleClose(pollId) {
      this.closingPollId = pollId;
      try {
        await this.closePoll(pollId);
      } finally {
        this.closingPollId = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.polls-tab {
  display: flex;
  flex-direction: column;
  height: 100%;

  &__scroll {
    flex: 1;
    overflow-y: auto;
    padding: 8px;
  }

  &__create-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 8px 0;
    margin-bottom: 8px;
    border: 1.5px dashed rgba(244, 119, 33, 0.35);
    border-radius: 10px;
    background: rgba(244, 119, 33, 0.03);
    color: #f47721;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover {
      background: rgba(244, 119, 33, 0.08);
      border-color: #f47721;
    }
  }

  &__poll-item {
    margin-bottom: 8px;
  }

  &__section-title {
    display: flex;
    align-items: center;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.4);
    margin: 8px 0 6px;
    letter-spacing: 0.08em;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    color: rgba(0, 0, 0, 0.35);
    font-size: 13px;
    text-align: center;
    padding: 28px 0;
  }
}
</style>
